import type { OrderStatus } from "@/types/enums/OrderStatus"
import type { PaymentTiming } from "@/types/enums/PaymentTiming"
import type { ChargeType } from "@/domain/order/charge-classification"
import type { SavingsSummary } from "@/domain/billing/billing-preview.model"
import { roundMoney } from "@/domain/money/round-money"

export { roundMoney } from "@/domain/money/round-money"

export type OrderDetailsCurrency = {
  code: string
  symbol: string
}

/** Semantic palette key → map to MUI in the view via {@link orderDetailsSemanticAccent}. */
export type OrderDetailsStatusSemanticColor = "blue" | "orange" | "green" | "gray" | "red" | "yellow" | "purple"

export type OrderDetailsStatusSemantic = {
  label: string
  color: OrderDetailsStatusSemanticColor
  /** Stable funnel stage for analytics / feature flags */
  stage: string
}

export type OrderDetailsItemModifier = {
  name: string
  quantity: number
  price: number
  total: number
  /** Present when API / catalog wiring can resolve reorder modifiers. */
  modifierGroupId?: string
  modifierGroupItemId?: string
  childMenuItemId?: string
}

export type OrderDetailsLineItem = {
  name: string
  quantity: number
  unitPrice: number
  /**
   * Backend reference / list unit price when provided (`reference_unit_price`).
   * Display-only — never used to invent savings amounts on the client.
   */
  referenceUnitPrice: number | null
  lineTotal: number
  modifiers: OrderDetailsItemModifier[]
  /** Menu catalog id — required for reorder pipeline. */
  menuItemId: string
  category: string
  /** Present when historical order includes combo selections (reorder blocked until supported). */
  comboSelections?: Array<{
    combo_group_id: string
    child_menu_item_id: string
    effective_price?: number
  }>
}

export type OrderDetailsCharge = {
  name: string
  amount: number
  chargeType: ChargeType
}

export type OrderDetailsDiscount = {
  name?: string
  amount: number
}

export type OrderDetailsSummary = {
  subTotal: number
  totalDiscount: number
  totalTax: number
  totalFees: number
  payableAmount: number
}

export type OrderDetailsTimelineEntry = {
  label: string
  /** ISO-ish timestamp from API (passed through as string). */
  time: string
}

/**
 * Normalized order details for My Orders → Order Details UI.
 * Populated only via {@link mapOrderDetailsApiData} — do not bind raw API shapes in components.
 */
export type OrderDetailsUI = {
  orderId: string
  /** Restaurant scope for cart lines / billing preview. */
  restaurantId: string
  orderNumber: number
  /** Human-readable status (legacy / API name). */
  status: string
  /** Stable status code / enum value as string (for keys, analytics). */
  statusCode: string
  /** Parsed enum for shared copy with order list. */
  orderStatus: OrderStatus
  /** Production-grade semantic status for hero + chips. */
  statusSemantic: OrderDetailsStatusSemantic
  customerName: string
  contactNumber: string
  address: string
  city: string
  country: string
  zipCode: string
  createdDate: string
  updatedDate: string
  /**
   * Customer-facing fulfillment label from backend (e.g. Delivery) — empty when omitted.
   * "Pickup" for Pickup orders (the backend lookup name is "TakeAway").
   */
  orderTypeLabel: string
  /**
   * Website Pickup / Takeaway order: true only when the persisted `order_type_id` is 2.
   * Never derived from checkout state, source, channel or labels. Absent = not Pickup.
   */
  isPickup?: boolean
  /**
   * Customer-facing channel label from backend (e.g. Placed via Website) — empty when omitted.
   */
  orderChannelLabel: string
  /**
   * Business date from backend when provided and distinct from placement time — empty when omitted.
   */
  businessDate: string
  /** Payment completion timestamp from backend when provided. */
  paymentPaidAt: string
  currency: OrderDetailsCurrency
  isPaid: boolean
  /** Uppercase gateway / order payment status (`UNKNOWN` when absent). */
  paymentStatus: string
  /** Human-readable payment status for chips. */
  paymentStatusDisplay: string
  /** Parsed enum for shared copy with order list. */
  paymentTiming: PaymentTiming
  /** Stable payment timing code / enum value as string (for keys, analytics). */
  paymentTimingCode: string
  /** Human-readable payment timing for chips and detail rows. */
  paymentTimingDisplay: string
  paymentMethod: string
  /**
   * Captured amount from `order_payment_details.amount` when the API sent it.
   * Null when there is no payment row or the amount was omitted.
   * Not derived from the bill, order status, or refunds.
   */
  paymentAmount?: number | null
  summary: OrderDetailsSummary
  items: OrderDetailsLineItem[]
  charges: OrderDetailsCharge[]
  discounts: OrderDetailsDiscount[]
  /**
   * Canonical customer savings from backend `savings_summary` (same {@link SavingsSummary} as billing preview).
   * `null` when the API omits it — never invent from `total_discount`.
   */
  savingsSummary: SavingsSummary | null
  /** When false, show simplified “amount due” billing block. */
  hasOrderBillSummary: boolean
  instructions: string
  billingSnapshotId: string | null
  paymentId: string | null
  paymentTypeDisplay: string
  timeline: OrderDetailsTimelineEntry[]
  /** Show hosted payment / retry CTA (not paid, payable, snapshot present). */
  canRetryPayment: boolean
  /**
   * Backend-provided customer-safe explanation (`cancellation.message`) for a Cancelled order.
   * `null`/absent when the order is not Cancelled or the API sends no cancellation — never
   * derived from status or a reason code on the client.
   */
  cancellationMessage?: string | null
}

/**
 * Money shown beside "Total paid" and "Amount paid".
 * A paid order uses the captured payment amount when the API sent one.
 * Unpaid orders, and paid orders with no payment amount, keep the bill payable.
 */
export function orderPaidDisplayAmount(order: OrderDetailsUI): number {
  if (order.isPaid && order.paymentAmount != null) return order.paymentAmount
  return order.summary.payableAmount
}

function formatWithSymbolFallback(order: OrderDetailsUI, safe: number): string {
  try {
    const sym = order.currency.symbol?.trim()
    const amount = roundMoney(safe).toFixed(2)
    return sym ? `${sym}${amount}` : amount
  } catch {
    return `${roundMoney(safe).toFixed(2)}`
  }
}

export function formatOrderCurrency(order: OrderDetailsUI, value: unknown): string {
  try {
    const n = Number(value)
    const safe = roundMoney(Number.isFinite(n) ? n : 0)
    const code = order.currency.code?.trim()
    if (code) {
      try {
        return new Intl.NumberFormat("en", {
          style: "currency",
          currency: code,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(safe)
      } catch {
        return formatWithSymbolFallback(order, safe)
      }
    }
    return formatWithSymbolFallback(order, safe)
  } catch {
    return formatWithSymbolFallback(order, 0)
  }
}
